import type { ReactElement } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@kula-ai/components';

interface ActivityTooltipProps {
  /** The copy. Falsy → the child is returned untouched, with no wrapper at all. */
  label?: string;
  children: ReactElement;
  'data-test-id'?: string;
}

/**
 * The one hover tooltip in the kit — the row's only way of saying the quiet part:
 * *why* a control is dead ("Survey cannot be edited"), or *when* something
 * happened ("Sent on 4 Jan at 10:30 IST", "Last reminder sent on …").
 *
 * Every consumer of it hits the same two traps, so they are solved once, here:
 *
 *   1. The `<span>` is load-bearing. A disabled `<button>` emits no pointer
 *      events, so a tooltip bound straight to it never fires — the wrapper is
 *      what stays hoverable. This is exactly the case that matters most, because
 *      a disabled control is precisely when the user needs the explanation.
 *   2. `delayDuration={150}`. The 700ms Radix default reads as broken on an
 *      icon-sized target (repo rule).
 *
 * Testing note: Radix never mounts `TooltipContent` in jsdom — no hover state
 * machine, no layout — so no test can open one. Suites therefore assert the COPY
 * HANDOFF (stub the overlay, check what we pass it, see `ActivityTooltip.test`)
 * and consumers assert DELEGATION (the wrapper is present). Whether the tooltip
 * visually appears is Radix's contract, and a browser's to prove.
 */
export const ActivityTooltip = ({
  label,
  children,
  'data-test-id': testId = 'activity-tooltip',
}: ActivityTooltipProps) => {
  if (!label) return children;

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span data-test-id={testId} style={{ display: 'inline-flex' }}>
            {children}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" collisionPadding={12}>
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};
